/**
 * Presenter keys: the key table shown in the help sheet and PORT_NOTES.md,
 * and the pure mapping from a keydown to a presenter command. The listener
 * that uses it is usePresenterKeys.ts.
 */
import { BEATS, beatIndexForKey } from './beats';

export type PresenterCommand =
  | { type: 'beat'; index: number }
  | { type: 'next' }
  | { type: 'prev' }
  | { type: 'tour' }
  | { type: 'freeze' }
  | { type: 'demo' }
  | { type: 'captions' }
  | { type: 'help' }
  | { type: 'escape' };

/** The key table, in the order the help sheet lists it. */
export const PRESENTER_KEYS: ReadonlyArray<{ keys: string; action: string }> = [
  { keys: '1 to 9, 0', action: `Jump to beat 1 to ${BEATS.length} (0 is beat ${BEATS.length})` },
  { keys: 'Space, Right, Page Down', action: 'Next beat' },
  { keys: 'Left, Page Up', action: 'Previous beat' },
  { keys: 'T', action: 'Start or stop the auto-tour' },
  { keys: 'X', action: 'Freeze or unfreeze every live update' },
  { keys: 'D', action: 'Demo data profile on or off' },
  { keys: 'H', action: 'Show or hide the captions' },
  { keys: '?', action: 'This help' },
  { keys: 'Esc', action: 'Close this help, else leave presenter mode' },
  { keys: 'P', action: 'Enter or leave presenter mode' },
];

/** Command for KeyboardEvent.key, or null when the key is not a presenter key. */
export function commandForKey(key: string): PresenterCommand | null {
  const index = beatIndexForKey(key);
  if (index >= 0) return { type: 'beat', index };
  switch (key) {
    case ' ':
    case 'Spacebar':
    case 'ArrowRight':
    case 'PageDown':
      return { type: 'next' };
    case 'ArrowLeft':
    case 'PageUp':
      return { type: 'prev' };
    case 't':
    case 'T':
      return { type: 'tour' };
    case 'x':
    case 'X':
      return { type: 'freeze' };
    case 'd':
    case 'D':
      return { type: 'demo' };
    case 'h':
    case 'H':
      return { type: 'captions' };
    case '?':
      return { type: 'help' };
    case 'Escape':
      return { type: 'escape' };
    default:
      return null;
  }
}

/** The parts of an element the focus checks read (an HTMLElement in the browser, a plain object in tests). */
export interface FocusTarget {
  tagName?: string;
  isContentEditable?: boolean;
  closest?: (selector: string) => unknown;
}

/** Containers that count as the GAIA column: the shell's aside and the presenter dock both carry these. */
export const GAIA_SELECTOR = '[data-gaia-root], [aria-label="GAIA assistant"]';

export function isTypingTarget(target: FocusTarget | null | undefined): boolean {
  if (!target) return false;
  const tag = target.tagName?.toUpperCase();
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable === true;
}

export function isInGaia(target: FocusTarget | null | undefined): boolean {
  return Boolean(target?.closest?.(GAIA_SELECTOR));
}

export interface KeyEventLike {
  key: string;
  ctrlKey: boolean;
  metaKey: boolean;
  altKey: boolean;
  target: FocusTarget | null;
}

export interface KeyContext {
  presenter: boolean;
  /** The element that has focus (document.activeElement). */
  focused: FocusTarget | null;
  helpOpen: boolean;
  /** Another dialog (not the presenter help) is open: Escape belongs to it. */
  dialogOpen: boolean;
}

/**
 * The command a keydown triggers, or null when presenter mode should leave
 * the event alone: presenter mode off, a modifier held (browser shortcuts),
 * typing in a field, focus inside GAIA, or Escape while another dialog is
 * open.
 */
export function interpretKey(event: KeyEventLike, ctx: KeyContext): PresenterCommand | null {
  if (!ctx.presenter) return null;
  if (event.ctrlKey || event.metaKey || event.altKey) return null;
  if (isTypingTarget(event.target) || isTypingTarget(ctx.focused)) return null;
  if (isInGaia(event.target) || isInGaia(ctx.focused)) return null;
  const command = commandForKey(event.key);
  if (!command) return null;
  if (command.type === 'escape' && !ctx.helpOpen && ctx.dialogOpen) return null;
  return command;
}
